import { normalizeDangerousWasteColumns } from './dangerousWasteRecordColumns.js';

export const WASTE_RECEIVERS_COLLECTION = 'waste_receivers';

export const DEFAULT_WASTE_RECEIVER_COLUMNS = [
    { id: 'constituent', display_name: 'Indhold' },
    { id: 'category', display_name: 'Kategori' },
    { id: 'un_number', display_name: 'UN-nr.' },
    { id: 'hazard_classes', display_name: 'Fareklasse(r)' },
    { id: 'instruction', display_name: 'Deklaration' },
];

export const DEFAULT_WASTE_RECEIVER_SORTING_LIST = {
    sortingItems: [],
    version: 0,
    lastUpdate: 0,
};

export const DEFAULT_WASTE_RECEIVER_NO_SHOW_COLUMNS = [];

export const DEFAULT_WASTE_RECEIVER_GROUPING_COLUMNS = [];

export const DEFAULT_WASTE_RECEIVER_READ_ALOUD_COLUMNS = [];

export const DEFAULT_WASTE_RECEIVER_DEPLOYMENT_CONFIG = {
    disclaimer: '',
};

export const DEFAULT_WASTE_RECEIVER_USER_EVALUATION_PRIORITIZATION = [];

const toText = (value) => {
    if (value === undefined || value === null) return '';
    return String(value).trim();
};

// Matches wasteReceiverSortingListTransition.js's normalizeDanishComparable. Duplicated
// (rather than imported) to avoid a circular import — that module already imports from
// this one.
const normalizeDanishComparable = (value) =>
    toText(value).trim().normalize('NFC').toLocaleLowerCase('da-DK');

// Setup now stores hidden/grouping/read-aloud columns by name rather than id (so id-less
// columns can be referenced too), but older receivers may still have ids stored from before
// that change. Resolve a stored reference against either, back to the column's canonical id.
function buildColumnIdLookup(columns) {
    const lookup = new Map();
    columns.forEach((column) => {
        const idKey = normalizeDanishComparable(column.id);
        const nameKey = normalizeDanishComparable(column.display_name);
        if (idKey) lookup.set(idKey, column.id);
        if (nameKey) lookup.set(nameKey, column.id);
    });
    return lookup;
}

function resolveColumnIdReferences(references, columns) {
    const lookup = buildColumnIdLookup(columns);
    const seen = new Set();

    return references
        .map((reference) => lookup.get(normalizeDanishComparable(reference)))
        .filter((columnId) => {
            if (!columnId || seen.has(columnId)) return false;
            seen.add(columnId);
            return true;
        });
}

const toNumber = (value, fallback = 0) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : fallback;
};

export function normalizeCompanyIds(companyIds = []) {
    const seen = new Set();

    return (Array.isArray(companyIds) ? companyIds : [])
        .map(toText)
        .filter((companyId) => {
            if (!companyId || seen.has(companyId)) return false;
            seen.add(companyId);
            return true;
        });
}

export function normalizeWasteReceiverColumns(columns = []) {
    return normalizeDangerousWasteColumns(columns);
}

export function normalizeWasteReceiverNoShowColumns(columnIds = []) {
    const seen = new Set();

    return (Array.isArray(columnIds) ? columnIds : [])
        .map(toText)
        .filter((columnId) => {
            if (!columnId || seen.has(columnId)) return false;
            seen.add(columnId);
            return true;
        });
}

export function normalizeWasteReceiverUserEvaluationPrioritization(userIds = [], allowedUserIds = null) {
    const seen = new Set();
    const allowed = Array.isArray(allowedUserIds) ? new Set(normalizeCompanyIds(allowedUserIds)) : null;

    return (Array.isArray(userIds) ? userIds : [])
        .map(toText)
        .filter((userId) => {
            if (!userId || seen.has(userId)) return false;
            if (allowed && !allowed.has(userId)) return false;
            seen.add(userId);
            return true;
        });
}

export function getVisibleWasteReceiverColumns(columns = [], noShowColumns = []) {
    const normalizedColumns = normalizeWasteReceiverColumns(columns);
    const hiddenColumnIds = new Set(resolveColumnIdReferences(
        normalizeWasteReceiverNoShowColumns(noShowColumns),
        normalizedColumns,
    ));
    return normalizedColumns.filter((column) => !hiddenColumnIds.has(column.id));
}

// Grouping columns are an unordered selection; their order is always derived from the
// visible column order, so a later column reorder can never leave a stale stored order.
export function normalizeWasteReceiverGroupingColumns(
    columnIds = [],
    columns = [],
    noShowColumns = [],
) {
    const visibleColumns = getVisibleWasteReceiverColumns(columns, noShowColumns);
    const orderByColumnId = new Map(
        visibleColumns.map((column, index) => [column.id, index]),
    );

    return resolveColumnIdReferences(
        (Array.isArray(columnIds) ? columnIds : []).map(toText),
        visibleColumns,
    )
        .filter((columnId) => orderByColumnId.has(columnId))
        .sort((left, right) => orderByColumnId.get(left) - orderByColumnId.get(right));
}

export function normalizeWasteReceiverReadAloudColumns(columnIds = [], columns = []) {
    const normalizedColumns = normalizeWasteReceiverColumns(columns);
    const orderByColumnId = new Map(
        normalizedColumns.map((column, index) => [column.id, index]),
    );

    return resolveColumnIdReferences(
        (Array.isArray(columnIds) ? columnIds : []).map(toText),
        normalizedColumns,
    )
        .filter((columnId) => orderByColumnId.has(columnId))
        .sort((left, right) => orderByColumnId.get(left) - orderByColumnId.get(right));
}

export function normalizeWasteReceiverSortingList(sortingList) {
    if (Array.isArray(sortingList)) {
        return {
            ...DEFAULT_WASTE_RECEIVER_SORTING_LIST,
            sortingItems: sortingList,
        };
    }

    if (!sortingList || typeof sortingList !== 'object') {
        return { ...DEFAULT_WASTE_RECEIVER_SORTING_LIST };
    }

    return {
        sortingItems: Array.isArray(sortingList.sorting_items)
            ? sortingList.sorting_items
            : Array.isArray(sortingList.sortingItems)
              ? sortingList.sortingItems
              : [],
        version: toNumber(sortingList.version, 0),
        lastUpdate: toNumber(sortingList.last_update ?? sortingList.lastUpdate, 0),
    };
}

const isPlainObject = (value) =>
    !!value && typeof value === 'object' && !Array.isArray(value);

// deployment_config is shared with the deployment side, which owns keys this dashboard
// knows nothing about. Unknown keys are therefore carried through untouched: dropping them
// here would silently delete those settings on every save from setup, since the receiver is
// written back from exactly what was loaded.
export function normalizeWasteReceiverDeploymentConfig(deploymentConfig) {
    if (!isPlainObject(deploymentConfig)) {
        return { ...DEFAULT_WASTE_RECEIVER_DEPLOYMENT_CONFIG };
    }

    const normalized = {
        ...deploymentConfig,
        disclaimer: typeof deploymentConfig.disclaimer === 'string'
            ? deploymentConfig.disclaimer
            : DEFAULT_WASTE_RECEIVER_DEPLOYMENT_CONFIG.disclaimer,
    };

    if (isPlainObject(deploymentConfig.llm_call)) {
        normalized.llm_call = {
            ...deploymentConfig.llm_call,
            system_prompt: typeof deploymentConfig.llm_call.system_prompt === 'string'
                ? deploymentConfig.llm_call.system_prompt
                : '',
        };
    } else {
        // An llm_call that is not a map would fail validWasteReceiverDeploymentConfig().
        delete normalized.llm_call;
    }

    if (isPlainObject(deploymentConfig.result_template)) {
        normalized.result_template = {
            ...deploymentConfig.result_template,
            html: typeof deploymentConfig.result_template.html === 'string'
                ? deploymentConfig.result_template.html
                : '',
            css: typeof deploymentConfig.result_template.css === 'string'
                ? deploymentConfig.result_template.css
                : '',
        };
    } else {
        // Same reasoning as llm_call: anything that is not a map is not writable.
        delete normalized.result_template;
    }

    return normalized;
}

export function getWasteReceiverSystemPrompt(deploymentConfig) {
    const { llm_call: llmCall } = normalizeWasteReceiverDeploymentConfig(deploymentConfig);
    return llmCall ? llmCall.system_prompt : '';
}

// llm_call is only written once there is a prompt to write (or the receiver already carries
// the map), so a receiver that makes no LLM call is never given an empty system prompt.
export function setWasteReceiverSystemPrompt(deploymentConfig, systemPrompt) {
    const normalized = normalizeWasteReceiverDeploymentConfig(deploymentConfig);
    const prompt = typeof systemPrompt === 'string' ? systemPrompt : '';

    if (!normalized.llm_call && !prompt) return normalized;

    return {
        ...normalized,
        llm_call: { ...normalized.llm_call, system_prompt: prompt },
    };
}

// The kiosk result screen for this receiver. A receiver that carries no result_template
// runs the kiosk's own built-in result.html/result.css, so "not set" is the ordinary state
// and is reported rather than filled in with a copy of those files.
export function getWasteReceiverResultTemplate(deploymentConfig) {
    const { result_template: resultTemplate } = normalizeWasteReceiverDeploymentConfig(deploymentConfig);

    if (!resultTemplate) {
        return { html: '', css: '', updatedAt: null, isCustom: false };
    }

    return {
        html: resultTemplate.html,
        css: resultTemplate.css,
        updatedAt: typeof resultTemplate.updated_at === 'number' ? resultTemplate.updated_at : null,
        isCustom: true,
    };
}

// Writing an empty html and css removes the map, which hands the receiver back to the
// kiosk's built-in screen. Without that, "clear the editor" would deploy a blank screen to
// the station instead of restoring the default.
export function setWasteReceiverResultTemplate(deploymentConfig, { html, css, updatedAt } = {}) {
    const normalized = normalizeWasteReceiverDeploymentConfig(deploymentConfig);
    const nextHtml = typeof html === 'string' ? html : '';
    const nextCss = typeof css === 'string' ? css : '';

    if (!nextHtml.trim() && !nextCss.trim()) {
        const { result_template: unused, ...rest } = normalized;
        return rest;
    }

    return {
        ...normalized,
        result_template: {
            ...normalized.result_template,
            html: nextHtml,
            css: nextCss,
            updated_at: typeof updatedAt === 'number' ? updatedAt : Date.now(),
        },
    };
}

export function normalizeWasteReceiverDoc(snapshot) {
    const data = snapshot?.data?.() || {};

    return {
        id: snapshot.id,
        name: toText(data.name),
        companies: normalizeCompanyIds(data.companies),
        columns: normalizeWasteReceiverColumns(data.columns),
        // LEGACY_FIELD_NAME: no_show_coloumns was a misspelling; no_show_columns is now the
        // field written going forward, but older documents may still only have the old name.
        noShowColumns: normalizeWasteReceiverNoShowColumns(
            data.no_show_columns ?? data.no_show_coloumns,
        ),
        groupingColumns: normalizeWasteReceiverGroupingColumns(
            data.grouping_columns ?? DEFAULT_WASTE_RECEIVER_GROUPING_COLUMNS,
            data.columns,
            data.no_show_columns ?? data.no_show_coloumns,
        ),
        readAloudColumns: normalizeWasteReceiverReadAloudColumns(
            data.read_aloud_columns ?? DEFAULT_WASTE_RECEIVER_READ_ALOUD_COLUMNS,
            data.columns,
        ),
        userEvaluationPrioritization: normalizeWasteReceiverUserEvaluationPrioritization(
            data.user_evaluation_prioritisation
                ?? data.user_evaluation_prioritization
                ?? DEFAULT_WASTE_RECEIVER_USER_EVALUATION_PRIORITIZATION,
        ),
        sortingList: normalizeWasteReceiverSortingList(data.sorting_list),
        deploymentConfig: normalizeWasteReceiverDeploymentConfig(data.deployment_config),
    };
}

function normalizeCompanyMemberships(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

    return Object.entries(value).reduce((next, [companyId, permissions]) => {
        const normalizedCompanyId = toText(companyId);
        if (!normalizedCompanyId) return next;
        next[normalizedCompanyId] = permissions && typeof permissions === 'object'
            ? permissions
            : {};
        return next;
    }, {});
}

function canMembershipViewWasteReceiverSortingList(permissions) {
    return (
        permissions?.can_view_waste_receiver_sorting_list === true ||
        permissions?.canViewWasteReceiverSortingList === true
    );
}

export function canUserAccessWasteReceiver(userData, wasteReceiver) {
    if (!userData || !wasteReceiver) return false;
    if (userData.isAdmin) return true;

    const userWasteReceiverIds = new Set(
        normalizeCompanyIds(userData.waste_receivers || userData.wasteReceivers || []),
    );
    const userCompanyIds = new Set(normalizeCompanyIds(userData.companies || []));
    const receiverCompanyIds = normalizeCompanyIds(wasteReceiver.companies || []);

    return (
        userWasteReceiverIds.has(wasteReceiver.id) ||
        receiverCompanyIds.some((companyId) => userCompanyIds.has(companyId))
    );
}

export function canUserManageWasteReceiverSortingList(userData, wasteReceiver) {
    if (!userData || !wasteReceiver) return false;
    if (userData.isAdmin) return true;

    const userWasteReceiverIds = new Set(
        normalizeCompanyIds(userData.waste_receivers || userData.wasteReceivers || []),
    );

    return userWasteReceiverIds.has(wasteReceiver.id);
}

export function canUserViewWasteReceiverSortingList(userData, wasteReceiver) {
    if (!userData || !wasteReceiver) return false;
    if (canUserManageWasteReceiverSortingList(userData, wasteReceiver)) return true;

    const userCompanyIds = new Set(normalizeCompanyIds(userData.companies || []));
    if (userCompanyIds.size === 0) return false;

    const companyMemberships = normalizeCompanyMemberships(userData.company_memberships);
    const sortingListViewCompanyIds = new Set(
        Object.entries(companyMemberships)
            .filter(([, permissions]) => canMembershipViewWasteReceiverSortingList(permissions))
            .map(([companyId]) => companyId),
    );
    if (sortingListViewCompanyIds.size === 0) return false;

    const receiverCompanyIds = normalizeCompanyIds(wasteReceiver.companies || []);

    return receiverCompanyIds.some((companyId) =>
        userCompanyIds.has(companyId) && sortingListViewCompanyIds.has(companyId),
    );
}

export function filterSortableWasteReceivers(userData, wasteReceivers = []) {
    return (Array.isArray(wasteReceivers) ? wasteReceivers : []).filter((wasteReceiver) =>
        canUserViewWasteReceiverSortingList(userData, wasteReceiver),
    );
}

export function filterAccessibleWasteReceivers(userData, wasteReceivers = []) {
    return (Array.isArray(wasteReceivers) ? wasteReceivers : []).filter((wasteReceiver) =>
        canUserAccessWasteReceiver(userData, wasteReceiver),
    );
}

export function filterManageableWasteReceivers(userData, wasteReceivers = []) {
    return (Array.isArray(wasteReceivers) ? wasteReceivers : []).filter((wasteReceiver) =>
        canUserManageWasteReceiverSortingList(userData, wasteReceiver),
    );
}

export function serializeWasteReceiver({
    id,
    name,
    companies,
    columns,
    noShowColumns,
    groupingColumns,
    readAloudColumns,
    userEvaluationPrioritization,
    sortingList,
    deploymentConfig,
}) {
    const normalizedSortingList = normalizeWasteReceiverSortingList(sortingList);
    const columnIds = new Set(normalizeWasteReceiverColumns(columns).map((column) => column.id));

    const payload = {
        id: toText(id),
        name: toText(name),
        companies: normalizeCompanyIds(companies),
        columns: normalizeWasteReceiverColumns(columns),
        // LEGACY_FIELD_NAME: no_show_coloumns was a misspelling; only the correctly spelled
        // no_show_columns is written from now on.
        no_show_columns: normalizeWasteReceiverNoShowColumns(noShowColumns)
            .filter((columnId) => columnIds.has(columnId)),
        user_evaluation_prioritisation: normalizeWasteReceiverUserEvaluationPrioritization(
            userEvaluationPrioritization,
        ),
        sorting_list: {
            sorting_items: normalizedSortingList.sortingItems,
            version: normalizedSortingList.version,
            last_update: normalizedSortingList.lastUpdate,
        },
    };

    if (groupingColumns !== undefined) {
        payload.grouping_columns = normalizeWasteReceiverGroupingColumns(
            groupingColumns,
            columns,
            noShowColumns,
        );
    }

    if (readAloudColumns !== undefined) {
        payload.read_aloud_columns = normalizeWasteReceiverReadAloudColumns(
            readAloudColumns,
            columns,
        );
    }

    if (deploymentConfig !== undefined) {
        payload.deployment_config = normalizeWasteReceiverDeploymentConfig(deploymentConfig);
    }

    return payload;
}
